// Material UI
import {
  Autocomplete,
  Box,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormLabel,
  Stack,
} from "@mui/material";
import {
  LocalizationProvider,
  MobileDatePicker,
  PickersDay,
  pickersDayClasses,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { Auth } from "aws-amplify";
import { useFormik } from "formik";
import Swal from "sweetalert2";

// React
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Redux
import { useSelector } from "react-redux";
import { selectUser } from "../Store/UserSlice";
import { selectProviders } from "../Store/ProvidersSlice";
import { selectPharmacies } from "../Store/PharmaciesSlice";
import { confirmButtonColor, boxGrey } from "../constants/ColourConstants";
//components
import CustomButton from "../constants/CustomButton";
import HealthCardForm from "./PatientRegistration/healthCardForm";
import { provinces } from "./PatientRegistration/provinceData";
import { authenticatedFetch } from "../api/utils/authenticatedFetch";
import patientValidation from "./patientValidation";
import { styles } from "./styles";

const CreatePatientForm = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const contacts = useSelector(selectPharmacies);
  const userData = useSelector(selectUser);
  const doctors = useSelector(selectProviders);
  let navigate = useNavigate();

  useEffect(() => {
    // Sets the user data.
    async function callPatientAPI() {
      const user = await Auth.currentAuthenticatedUser();
      setUser(user);
    }
    callPatientAPI();
  }, []);

  async function checkPatientExist(values) {
    const data = await authenticatedFetch(
      `https://${
        process.env.REACT_APP_ENV_ACCURO_GATEWAY
      }.execute-api.ca-central-1.amazonaws.com/${
        process.env.REACT_APP_ENV
      }/search-patients?phn=${values.phin}&dob=${JSON.stringify(
        values.dateOfBirth
      )}&regNumber=${values.regNumber}&userId=${user.attributes["sub"]}`,
      {
        method: "GET",
        headers: {
          Accept: "*/*",
        },
      }
    ).then((response) => {
      return response.json();
    });
    return data;
  }

  /**
   * Sends an create patient request to Accuro based on form values.
   *
   * @param {object} body Request data to be sent.
   * @return {boolean} Request result.
   */
  async function addPatient(body) {
    setLoading(true);
    const checkPatientExists = await checkPatientExist(body);
    if (checkPatientExists) {
      Swal.fire({
        title: "Patient already exists",
        text: "This patient has an active profile. Please search for the patient using the correct health card number and date of birth before creating a new profile, or try again with different information.",
        icon: "warning",
        confirmButtonColor: confirmButtonColor,
        confirmButtonText: "Return to Home",
        showDenyButton: true,
        denyButtonColor: confirmButtonColor,
        denyButtonText: `Try Again`,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/");
        }
      });
    } else {
      const data = await authenticatedFetch(
        `https://${process.env.REACT_APP_ENV_ACCURO_GATEWAY}.execute-api.ca-central-1.amazonaws.com/${process.env.REACT_APP_ENV}/add-patients`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        }
      ).then((response) => {
        return response.json();
      });
      if (!data.error && data.message !== "Internal server error") {
        await addPatientCard({
          patientId: data,
          phin: body.phin,
          firstName: body.firstName,
          lastName: body.lastName,
          userId: user.attributes["sub"],
          userEmail: user.attributes["email"],
        });
        Swal.fire({
          title: "Patient Added",
          text: "You have Added the patient.",
          icon: "success",
          confirmButtonColor: confirmButtonColor,
        });
        navigate("/");
      } else {
        Swal.fire({
          title: "Oops! Please try again!",
          text: data.error ?? data.message,
          icon: "error",
          confirmButtonColor: confirmButtonColor,
        });
      }
    }

    setLoading(false);
  }

  /**
   * Creates a patient card in the Dynamo database.
   *
   * @param {object} requestBody Request data to be sent.
   */
  async function addPatientCard(requestBody) {
    await authenticatedFetch(
      `https://${process.env.REACT_APP_ENV_ACCURO_GATEWAY}.execute-api.ca-central-1.amazonaws.com/${process.env.REACT_APP_ENV}/save-data`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }
    ).then((response) => {
      return response.json();
    });
  }

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      middleName: "",
      dateOfBirth: userData.dob ? JSON.parse(userData.dob) : "",
      sex: "",
      address: "",
      city: "",
      postalCode: "",
      province: "",
      phoneNumber: "",
      landlineNumber: "",
      email: "",
      noHealthCard: userData.healthCard ? false : true,
      phin: userData.phin ? userData.phin : "",
      regNumber: userData.regNumber ? userData.regNumber : "",
      healthCardProvince: userData.healthCardProvince
        ? userData.healthCardProvince
        : "",
      pharmacy: "",
      doctor: "",
      allergies: "",
      patientNotes: "",
    },
    validationSchema: patientValidation,

    onSubmit: async (values) => {
      await addPatient(values);
    },
  });

  const sexes = ["Male", "Female", "Other"];

  return (
    <>
      {doctors.allProviders && contacts.allPharmacies ? (
        <Container maxWidth="lg">
          <Box sx={{ m: 3, p: 3, background: boxGrey, borderRadius: "16px" }}>
            <Typography sx={styles.typographyTitle}>
              Patient Registration
            </Typography>
            <Stack my={1}>
              <FormLabel sx={styles.typographySubtitleGrey}>
                General Information
              </FormLabel>
            </Stack>
            <Box component="form" onSubmit={formik.handleSubmit}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="firstName"
                    name="firstName"
                    type="text"
                    variant="filled"
                    label="First Name"
                    required
                    value={formik.values.firstName}
                    onChange={(e) => {
                      const capitalizedValue =
                        e.target.value.charAt(0).toUpperCase() +
                        e.target.value.slice(1);
                      formik.handleChange(e);
                      formik.setFieldValue("firstName", capitalizedValue); //Manually set the capitalized value
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.firstName &&
                      Boolean(formik.errors.firstName)
                    }
                    helperText={
                      formik.touched.firstName && formik.errors.firstName
                    }
                    inputProps={{ maxLength: 15 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="lastName"
                    name="lastName"
                    type="text"
                    label="Last Name"
                    required
                    variant="filled"
                    value={formik.values.lastName}
                    onChange={(e) => {
                      const capitalizedValue =
                        e.target.value.charAt(0).toUpperCase() +
                        e.target.value.slice(1);
                      formik.handleChange(e);
                      formik.setFieldValue("lastName", capitalizedValue); //Manually set the capitalized value
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.lastName && Boolean(formik.errors.lastName)
                    }
                    helperText={
                      formik.touched.lastName && formik.errors.lastName
                    }
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>
              </Grid>
              <Grid
                container
                spacing={2}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} fullWidth>
                    <MobileDatePicker
                      label="Date of Birth"
                      disableFuture
                      TextFieldProps={{ variant: "filled" }}
                      id="dateOfBirth"
                      name="dateOfBirth"
                      labelId="date-of-birth-select-label"
                      inputFormat="YYYY-MM-DD"
                      value={formik.values.dateOfBirth}
                      onChange={(value) =>
                        formik.setFieldValue("dateOfBirth", value)
                      }
                      openTo="year"
                      views={["year", "month", "day"]}
                      renderInput={(params) => (
                        <TextField
                          fullWidth
                          {...params}
                          required
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.dateOfBirth &&
                            Boolean(formik.errors.dateOfBirth)
                          }
                          helperText={
                            formik.touched.dateOfBirth &&
                            formik.errors.dateOfBirth
                          }
                        />
                      )}
                      renderDay={(day, _value, DayComponentProps) => {
                        return (
                          <PickersDay
                            {...DayComponentProps}
                            sx={{
                              [`&&.${pickersDayClasses.selected}`]: {
                                backgroundColor: "#005470",
                              },
                            }}
                          />
                        );
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="filled">
                    <InputLabel
                      id="sex-select-label"
                      required
                      error={formik.touched.sex && Boolean(formik.errors.sex)}
                    >
                      Sex
                    </InputLabel>
                    <Select
                      id="sex"
                      labelId="sex-select-label"
                      name="sex"
                      variant="filled"
                      sx={styles.leftAlignedSelect}
                      value={formik.values.sex}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.sex && Boolean(formik.errors.sex)}
                    >
                      {sexes.map((sex, i) => (
                        <MenuItem key={i + 1} value={i + 1}>
                          {sex}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText
                      error={formik.touched.sex && Boolean(formik.errors.sex)}
                    >
                      {formik.touched.sex && formik.errors.sex}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="address"
                    name="address"
                    type="text"
                    label="Address"
                    required
                    variant="filled"
                    value={formik.values.address}
                    onChange={(e) => {
                      const capitalizedValue =
                        e.target.value.charAt(0).toUpperCase() +
                        e.target.value.slice(1);
                      formik.handleChange(e);
                      formik.setFieldValue("address", capitalizedValue); //Manually set the capitalized value
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.address && Boolean(formik.errors.address)
                    }
                    helperText={formik.touched.address && formik.errors.address}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="city"
                    name="city"
                    type="text"
                    label="City"
                    required
                    variant="filled"
                    value={formik.values.city}
                    onChange={(e) => {
                      const capitalizedValue =
                        e.target.value.charAt(0).toUpperCase() +
                        e.target.value.slice(1);
                      formik.handleChange(e);
                      formik.setFieldValue("city", capitalizedValue); //Manually set the capitalized value
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="postalCode"
                    name="postalCode"
                    type="text"
                    label="Postal Code"
                    variant="filled"
                    required
                    value={formik.values.postalCode}
                    onChange={(e) => {
                      const capitalizedValue =
                        e.target.value.charAt(0).toUpperCase() +
                        e.target.value.slice(1);
                      formik.handleChange(e);
                      formik.setFieldValue("postalCode", capitalizedValue); //Manually set the capitalized value
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.postalCode &&
                      Boolean(formik.errors.postalCode)
                    }
                    helperText={
                      formik.touched.postalCode && formik.errors.postalCode
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="filled">
                    <InputLabel
                      id="province-select-label"
                      required
                      error={
                        formik.touched.province &&
                        Boolean(formik.errors.province)
                      }
                    >
                      Province
                    </InputLabel>
                    <Select
                      labelId="province-select-label"
                      id="province"
                      name="province"
                      sx={styles.leftAlignedSelect}
                      value={formik.values.province}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.province &&
                        Boolean(formik.errors.province)
                      }
                    >
                      {provinces.map((province) => (
                        <MenuItem key={province} value={province}>
                          {province}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText
                      error={
                        formik.touched.province &&
                        Boolean(formik.errors.province)
                      }
                    >
                      {formik.touched.province && formik.errors.province}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="phoneNumber"
                    name="phoneNumber"
                    type="text"
                    label="Best Cell Phone Number to Contact You"
                    variant="filled"
                    value={formik.values.phoneNumber}
                    onChange={(e) =>
                      /^[0-9]{0,10}$/.test(e.target.value) &&
                      formik.setFieldValue("phoneNumber", e.target.value)
                    }
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.phoneNumber &&
                      Boolean(formik.errors.phoneNumber)
                    }
                    helperText={
                      formik.touched.phoneNumber && formik.errors.phoneNumber
                    }
                    inputProps={{
                      maxLength: 10,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    type="text"
                    label="Best Email to Contact You"
                    variant="filled"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="landlineNumber"
                    name="landlineNumber"
                    type="text"
                    label="Landline Number"
                    variant="filled"
                    value={formik.values.landlineNumber}
                    onChange={(e) =>
                      /^[0-9]{0,10}$/.test(e.target.value) &&
                      formik.setFieldValue("landlineNumber", e.target.value)
                    }
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.landlineNumber &&
                      Boolean(formik.errors.landlineNumber)
                    }
                    helperText={
                      formik.touched.landlineNumber &&
                      formik.errors.landlineNumber
                    }
                    inputProps={{
                      maxLength: 10,
                    }}
                  />
                  <InputLabel
                    sx={{
                      color: "#da271e",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    Landline numbers will not receive confirmation text.
                  </InputLabel>
                </Grid>
              </Grid>
              <HealthCardForm formik={formik} userData={userData} />

              <Stack mb={1} mt={2}>
                <FormLabel sx={styles.typographySubtitleGrey}>
                  Additional Information
                </FormLabel>
              </Stack>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={contacts.allPharmacies}
                    getOptionLabel={(option) => option?.contactName ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option.contactId === value?.contactId
                    }
                    value={
                      formik.values.pharmacy !== "" &&
                      formik.values.pharmacy !== null
                        ? contacts.allPharmacies.find((pharmacy) => {
                            return (
                              pharmacy.contactId === formik.values.pharmacy
                            );
                          })
                        : null
                    }
                    onChange={(e, value) =>
                      formik.setFieldValue(
                        "pharmacy",
                        value ? value.contactId : null
                      )
                    }
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.contactId}>
                        {option.contactName}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Preferred Pharmacy"
                        variant="filled"
                        fullWidth
                      />
                    )}
                    disabled={contacts.allPharmacies.length === 0}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={doctors.uniqueProviders}
                    getOptionLabel={(option) =>
                      option.length === 0 ? "" : option.fullName
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={
                      formik.values.doctor !== "" &&
                      formik.values.doctor !== null
                        ? doctors.uniqueProviders.find((doctor) => {
                            return doctor.id === formik.values.doctor;
                          })
                        : null
                    }
                    onChange={(e, value) =>
                      formik.setFieldValue("doctor", value ? value.id : null)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Family Doctor"
                        variant="filled"
                        fullWidth
                      />
                    )}
                    disabled={doctors.uniqueProviders.length === 0}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={12}>
                  <TextField
                    fullWidth
                    id="allergies"
                    name="allergies"
                    type="text"
                    label="Allergies"
                    variant="filled"
                    multiline
                    rows={2}
                    value={formik.values.allergies}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.allergies &&
                      Boolean(formik.errors.allergies)
                    }
                    helperText={
                      formik.touched.allergies && formik.errors.allergies
                    }
                    inputProps={{
                      maxLength: 100,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    fullWidth
                    id="patientNotes"
                    name="patientNotes"
                    type="text"
                    label="Notes (Include any relevant medications and medical history)"
                    placeholder="Include any relevant medications and medical history"
                    variant="filled"
                    multiline
                    rows={3}
                    value={formik.values.patientNotes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.patientNotes &&
                      Boolean(formik.errors.patientNotes)
                    }
                    helperText={
                      formik.touched.patientNotes && formik.errors.patientNotes
                    }
                    inputProps={{
                      maxLength: 100,
                    }}
                  />
                </Grid>
              </Grid>

              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                spacing={2}
                sx={styles.buttonStack}
              >
                <CustomButton
                  label="Cancel"
                  type="secondary"
                  onClick={() => navigate("/")}
                />
                <CustomButton
                  onClick={formik.handleSubmit}
                  disabled={formik.isSubmitting}
                  label="Save"
                />
              </Stack>
            </Box>
          </Box>
        </Container>
      ) : (
        <Box
          sx={{
            display: `${loading ? "flex" : "none"}`,
            width: "center",
            height: 600,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      )}
    </>
  );
};
export default CreatePatientForm;
